import { useRef, useState } from "react";
import { Camera, Images, X } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { toast } from "./ui/sonner";

export default function PhotoCapture({ auditId, siteId, responseId, requirementId, existingPhotos = [], onUploaded, onDeleted, readOnly = false }) {
  const inputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [pending, setPending] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleFiles = (files) => {
    const arr = Array.from(files).map((file) => ({ file, preview: URL.createObjectURL(file), caption: "" }));
    setPending((prev) => [...prev, ...arr]);
  };

  const updateCaption = (idx, caption) => {
    setPending((prev) => prev.map((p, i) => (i === idx ? { ...p, caption } : p)));
  };

  const removePending = (idx) => {
    setPending((prev) => prev.filter((_, i) => i !== idx));
  };

  const uploadAll = async () => {
    setUploading(true);
    try {
      for (const item of pending) {
        const form = new FormData();
        form.append("audit_id", auditId);
        form.append("site_id", siteId);
        form.append("response_id", responseId || "");
        form.append("requirement_id", requirementId || "");
        form.append("type_preuve", "PHOTO_CONSTAT");
        form.append("description", item.caption);
        form.append("file", item.file);
        const res = await api.post("/preuves", form, { headers: { "Content-Type": "multipart/form-data" } });
        onUploaded?.(res.data);
      }
      setPending([]);
      toast.success("Photo(s) envoyée(s) vers Google Drive");
    } catch (e) {
      toast.error("Échec de l'envoi de la photo");
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (preuveId) => {
    try {
      await api.delete(`/preuves/${preuveId}`);
      onDeleted?.(preuveId);
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  return (
    <div className="space-y-3">
      {!readOnly && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="photo-capture-input"
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="photo-gallery-input"
          />
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1 gap-2" data-testid="photo-capture-button" onClick={() => inputRef.current?.click()}>
              <Camera className="h-4 w-4" /> Prendre une photo
            </Button>
            <Button type="button" variant="outline" className="flex-1 gap-2" data-testid="photo-gallery-button" onClick={() => galleryInputRef.current?.click()}>
              <Images className="h-4 w-4" /> Depuis la galerie
            </Button>
          </div>
        </>
      )}

      {existingPhotos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {existingPhotos.map((p) => (
            <div key={p.preuve_id} className="relative group" data-testid="photo-existing-thumb">
              <a href={p.drive_url} target="_blank" rel="noreferrer">
                <div className="aspect-square rounded-md border bg-muted flex items-center justify-center text-[10px] text-muted-foreground overflow-hidden">
                  {p.mime_type?.startsWith("image/") ? "📷 Photo" : "📄 Fichier"}
                </div>
              </a>
              {!readOnly && (
                <button
                  onClick={() => deletePhoto(p.preuve_id)}
                  data-testid="photo-existing-delete-button"
                  className="absolute -top-1 -right-1 bg-rose-600 text-white rounded-full p-0.5 opacity-80 hover:opacity-100"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!readOnly && pending.length > 0 && (
        <div className="space-y-2">
          {pending.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 border rounded-md p-2">
              <img src={item.preview} alt="preview" className="h-14 w-14 object-cover rounded-md" />
              <Input
                placeholder="Légende (optionnel)"
                value={item.caption}
                onChange={(e) => updateCaption(idx, e.target.value)}
                data-testid="photo-caption-input"
              />
              <button onClick={() => removePending(idx)} data-testid="photo-pending-remove-button">
                <X className="h-4 w-4 text-rose-500" />
              </button>
            </div>
          ))}
          <Button type="button" onClick={uploadAll} disabled={uploading} data-testid="photo-upload-confirm-button" className="w-full">
            {uploading ? "Envoi en cours..." : `Envoyer ${pending.length} photo(s)`}
          </Button>
        </div>
      )}
    </div>
  );
}
